import Actions from './Actions';
import { AddState, Dispatch } from './AddContext';
import type Client from 'App/resources/Model/Client';
import type ProspectingClient from 'App/resources/Model/ProspectingClient';
import type SoliditetClient from 'App/resources/Model/SoliditetClient';
import { findAndRemoveItem } from 'Store/helpers/array';
import { TABS } from '../Tabs/Tabs';

type SelectableClient = Client & { title: string };

const deselectClient = (dispatch: Dispatch, state: AddState, clientId: number) => {
	const result = findAndRemoveItem(state.selectedClients, { id: clientId });
	dispatch({
		type: Actions.DESELECT_CLIENT,
		selectedClients: result
	});
};

export const selectClient = (selectedClient: SelectableClient) => (dispatch: Dispatch, state: AddState) => {
	const alreadySelected = state.selectedClients.some(client => client.id === selectedClient.id);
	if (alreadySelected) {
		deselectClient(dispatch, state, selectedClient.id);
		return;
	}
	dispatch({
		type: Actions.SET_SELECTED_CLIENTS,
		selectedClients: [selectedClient, ...state.selectedClients]
	});
};

export const deselectAll = () => (dispatch: Dispatch) => {
	dispatch({
		type: Actions.DESELECT_ALL
	});
};

export const handleBranchOfficesChange =
	(
		branches: ProspectingClient[] | SoliditetClient[],
		prevSelectedBranches: ProspectingClient[] | SoliditetClient[],
		filtered?: ProspectingClient[] | SoliditetClient[]
	) =>
	(dispatch: Dispatch, state: AddState) => {
		// Early return for non-prospecting case
		if (!state.isProspecting) {
			dispatch({ type: Actions.SET_SELECTED_BRANCHES, selectedBranches: branches });
			if (filtered) {
				dispatch({ type: Actions.SET_FILTERED_BRANCH_OFFICES, filteredBranchOffices: filtered });
			}
			return;
		}

		// Check if client has a valid Upsales match
		const hasMatchInUpsales = (branch: ProspectingClient | SoliditetClient): branch is ProspectingClient => {
			return 'matchInUpsales' in branch && branch.matchInUpsales !== null;
		};

		// Check if a client has a valid Upsales match and if it matches a specific ID
		const clientHasMatchWithId = (client: ProspectingClient | SoliditetClient, id: number): boolean => {
			return hasMatchInUpsales(client) && client.matchInUpsales?.id === id;
		};

		// Find new clients from branches that have Upsales matches
		const newClients = branches
			.filter(
				(branch): branch is ProspectingClient =>
					hasMatchInUpsales(branch) &&
					!state.selectedClients.some(({ id }) => id === branch.matchInUpsales?.id)
			)
			.map(
				branch =>
					({
						...branch.matchInUpsales,
						title: branch.name
					} as SelectableClient)
			);

		// Find clients to remove
		const clientsToRemove = state.selectedClients.filter(selectedClient => {
			const wasPreviouslySelected = prevSelectedBranches?.some(client =>
				clientHasMatchWithId(client, selectedClient.id)
			);

			const isStillSelectedInBranches = branches.some(branch => clientHasMatchWithId(branch, selectedClient.id));

			return wasPreviouslySelected && !isStillSelectedInBranches;
		});

		// Update state if there are changes
		if (newClients.length || clientsToRemove.length) {
			const remainingClients = state.selectedClients.filter(
				({ id }) => !clientsToRemove.some(cr => cr.id === id)
			);
			dispatch({ type: Actions.SET_SELECTED_CLIENTS, selectedClients: [...remainingClients, ...newClients] });
		}

		// Update branches and filtered branches
		dispatch({ type: Actions.SET_SELECTED_BRANCHES, selectedBranches: branches });
		if (filtered) {
			dispatch({ type: Actions.SET_FILTERED_BRANCH_OFFICES, filteredBranchOffices: filtered });
		}
	};

export const setCompanies =
	(selectable: SelectableClient[], nonSelectable: SelectableClient[]) => (dispatch: Dispatch) => {
		dispatch({
			type: Actions.SET_COMPANIES,
			selectableCompanies: selectable,
			nonSelectableCompanies: nonSelectable
		});
	};

export const setSelectedBranches = (branches: ProspectingClient[] | SoliditetClient[]) => (dispatch: Dispatch) => {
	dispatch({ type: Actions.SET_SELECTED_BRANCHES, selectedBranches: branches });
};

export const setSaving = (saving: boolean) => (dispatch: Dispatch) => {
	dispatch({ type: Actions.SET_SAVING, saving });
};

export const setActiveTab = (activeTab: TABS) => (dispatch: Dispatch) => {
	dispatch({ type: Actions.SET_ACTIVE_TAB, activeTab });
};

export const setSearchQuery = (searchQuery: string) => (dispatch: Dispatch) => {
	dispatch({ type: Actions.SET_SEARCH_QUERY, searchQuery });
};

export const setShowPreview = (showPreview: boolean) => (dispatch: Dispatch) => {
	dispatch({ type: Actions.SET_SHOW_PREVIEW, showPreview });
};

export const selectAllCompanies = (companies: SelectableClient[]) => (dispatch: Dispatch, state: AddState) => {
	const newSelectedClients = [...state.selectedClients];
	const existingIds = new Set(newSelectedClients.map(c => c.id));

	for (const company of companies) {
		if (!existingIds.has(company.id)) {
			newSelectedClients.push(company);
			existingIds.add(company.id);
		}
	}

	dispatch({
		type: Actions.SET_SELECTED_CLIENTS,
		selectedClients: newSelectedClients
	});
};

export const deselectAllCompanies = (companies: SelectableClient[]) => (dispatch: Dispatch, state: AddState) => {
	const companyIdsToRemove = new Set(companies.map(c => c.id));
	const newSelectedClients = state.selectedClients.filter(c => !companyIdsToRemove.has(c.id));

	dispatch({
		type: Actions.SET_SELECTED_CLIENTS,
		selectedClients: newSelectedClients
	});
};
